import { Currency } from './types'

const DECIMAL_PATTERN = /^\d+(?:\.\d+)?$/

export const normalizeDecimalInput = (value: string): string =>
  value.trim().replace(',', '.')

export const trimDecimalTrailingZeros = (value: string): string => {
  const normalized = normalizeDecimalInput(value)
  if (!DECIMAL_PATTERN.test(normalized)) return value

  const [integer, fraction] = normalized.split('.')
  const significantFraction = fraction?.replace(/0+$/, '')

  return significantFraction ? `${integer}.${significantFraction}` : integer
}

type DecimalParts = {
  value: string
  scale: number
}

const parseDecimal = (input: string): DecimalParts | null => {
  const value = normalizeDecimalInput(input)

  if (!DECIMAL_PATTERN.test(value)) return null

  const [integer, fraction = ''] = value.split('.')

  return { value: trimLeadingZeros(`${integer}${fraction}`), scale: fraction.length }
}

const trimLeadingZeros = (value: string): string =>
  value.replace(/^0+(?=\d)/, '')

const incrementInteger = (value: string): string => {
  const digits = value.split('')

  for (let index = digits.length - 1; index >= 0; index -= 1) {
    if (digits[index] !== '9') {
      digits[index] = String.fromCharCode(digits[index].charCodeAt(0) + 1)
      return digits.join('')
    }

    digits[index] = '0'
  }

  return `1${digits.join('')}`
}

const multiplyIntegers = (first: string, second: string): string => {
  const result = Array(first.length + second.length).fill(0)

  for (let firstIndex = first.length - 1; firstIndex >= 0; firstIndex -= 1) {
    const firstDigit = first.charCodeAt(firstIndex) - 48

    for (
      let secondIndex = second.length - 1;
      secondIndex >= 0;
      secondIndex -= 1
    ) {
      const secondDigit = second.charCodeAt(secondIndex) - 48
      result[firstIndex + secondIndex + 1] += firstDigit * secondDigit
    }
  }

  for (let index = result.length - 1; index > 0; index -= 1) {
    result[index - 1] += Math.floor(result[index] / 10)
    result[index] %= 10
  }

  return trimLeadingZeros(result.join(''))
}

const addIntegers = (first: string, second: string): string => {
  let carry = 0
  let result = ''
  const length = Math.max(first.length, second.length)

  for (let index = 0; index < length; index += 1) {
    const firstDigit = Number(first[first.length - index - 1] ?? 0)
    const secondDigit = Number(second[second.length - index - 1] ?? 0)
    const sum = firstDigit + secondDigit + carry
    result = `${sum % 10}${result}`
    carry = Math.floor(sum / 10)
  }

  return carry > 0 ? `${carry}${result}` : trimLeadingZeros(result)
}

const formatScaledDecimal = (value: string, scale: number): string => {
  if (scale === 0) return value

  const digits = value.padStart(scale + 1, '0')
  return `${digits.slice(0, -scale)}.${digits.slice(-scale)}`
}

const formatLocalizedDecimal = (integer: string, fraction?: string): string => {
  const groupedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')

  return fraction ? `${groupedInteger},${fraction}` : groupedInteger
}

const roundToScale = (value: string, scale: number, targetScale: number) => {
  if (scale <= targetScale) return `${value}${'0'.repeat(targetScale - scale)}`

  const removedDigits = scale - targetScale
  const rounded = value.slice(0, -removedDigits) || '0'
  const firstRemovedDigit = value.charCodeAt(value.length - removedDigits) - 48

  return firstRemovedDigit >= 5 ? incrementInteger(rounded) : rounded
}

export const formatDecimal = (value: string, fractionDigits = 2): string => {
  const decimal = parseDecimal(value)
  if (!decimal) return value

  return formatScaledDecimal(
    roundToScale(decimal.value, decimal.scale, fractionDigits),
    fractionDigits,
  )
}

export const formatAmount = (value: string, currency?: string): string => {
  const normalized = trimDecimalTrailingZeros(value)
  const decimal = parseDecimal(
    currency === 'USD' ? formatDecimal(normalized) : normalized,
  )
  if (!decimal) return value

  const formatted = formatScaledDecimal(decimal.value, decimal.scale)
  const [integer, fraction] = formatted.split('.')
  const fractionForDisplay =
    currency === 'USD' ? fraction : fraction?.replace(/0+$/, '')

  return formatLocalizedDecimal(integer, fractionForDisplay)
}

export const formatUsdAmount = (value: string): string => {
  const [integer, fraction] = formatDecimal(value).split('.')

  return formatLocalizedDecimal(integer, fraction)
}

export const getUsdEquivalent = (
  amount: string,
  currency: string,
  currencies: Currency[],
  approvedRate: string | null = null,
): string | null => {
  if (currency === 'USD') return null

  const rate =
    approvedRate ?? currencies.find((item) => item.code === currency)?.rate
  const amountDecimal = parseDecimal(amount)
  const rateDecimal = rate ? parseDecimal(rate) : null

  if (!amountDecimal || !rateDecimal) return null

  return formatScaledDecimal(
    roundToScale(
      multiplyIntegers(amountDecimal.value, rateDecimal.value),
      amountDecimal.scale + rateDecimal.scale,
      2,
    ),
    2,
  )
}

export const getUsdEstimate = (
  amount: string,
  currency: string,
  currencies: Currency[],
): string | null => {
  if (currency === 'USD') {
    const decimal = parseDecimal(amount)
    return decimal
      ? formatScaledDecimal(roundToScale(decimal.value, decimal.scale, 2), 2)
      : null
  }

  return getUsdEquivalent(amount, currency, currencies)
}

export const sumUsdEstimates = (values: Array<string | null>): string | null => {
  const estimates = values.filter((value): value is string => value !== null)
  if (estimates.length === 0) return null

  const sum = estimates.reduce((total, value) => {
    const decimal = parseDecimal(value)
    if (!decimal) return total

    return addIntegers(total, roundToScale(decimal.value, decimal.scale, 2))
  }, '0')

  return formatScaledDecimal(sum, 2)
}
